export type PublicHost = {
  slug: string;
  display_name: string;
  domain_label: string;
  role: string;
  bio: string;
  timezone: string;
};

export type PublicEventType = {
  slug: string;
  title: string;
  description: string;
  duration_minutes: number;
  minimum_notice_minutes: number;
  booking_horizon_days: number;
  location_label: string;
};

export type PublicBookingPage = {
  organization: {
    name: string;
    slug: string;
    timezone: string;
  };
  host: PublicHost;
  event_type: PublicEventType;
};

export type AvailableSlot = {
  starts_at: string;
  ends_at: string;
};

export type BookingConfirmation = {
  booking_id: string;
  reference: string;
  event_slug: string;
  manage_token: string;
  starts_at: string;
  ends_at: string;
  title: string;
  host_name: string;
  guest_name: string;
  guest_email: string;
  guest_timezone: string;
  location_label: string;
  meeting_url: string | null;
};

export type ManagedBooking = BookingConfirmation & {
  status: "confirmed" | "cancelled" | "completed" | "no_show";
  guest_notes: string | null;
  cancelled_at: string | null;
};

export type DashboardBooking = {
  id: number;
  public_id: string;
  reference: string;
  organization_id: number;
  host_profile_id: number;
  event_type_id: number;
  status: "confirmed" | "cancelled" | "completed" | "no_show";
  title: string;
  starts_at: string;
  ends_at: string;
  guest_name: string;
  guest_email: string;
  guest_timezone: string;
  guest_notes: string | null;
  meeting_url: string | null;
  location_label: string;
  created_at: string;
  host_profiles?: { display_name: string; domain_label: string } | null;
};

export type DashboardNotification = {
  id: number;
  booking_id: number;
  organization_id: number;
  kind: "booking_created" | "booking_cancelled" | "booking_rescheduled" | "meeting_updated";
  title: string;
  body: string;
  read_at: string | null;
  created_at: string;
};

export type HostProfile = {
  id: number;
  organization_id: number;
  user_id: string | null;
  slug: string;
  display_name: string;
  domain_label: string;
  role: string;
  bio: string;
  timezone: string;
  is_active: boolean;
};

export type AvailabilityRule = {
  id?: number;
  organization_id: number;
  host_profile_id: number;
  weekday: number;
  start_time: string;
  end_time: string;
  timezone: string;
  is_active: boolean;
};

export type DashboardEventType = {
  id: number;
  organization_id: number;
  host_profile_id: number;
  slug: string;
  title: string;
  description: string;
  duration_minutes: number;
  slot_interval_minutes: number;
  buffer_before_minutes: number;
  buffer_after_minutes: number;
  minimum_notice_minutes: number;
  booking_horizon_days: number;
  location_label: string;
  meeting_url: string | null;
  is_active: boolean;
};

export type ApiErrorCode =
  | "configuration_error"
  | "invalid_request"
  | "not_found"
  | "slot_unavailable"
  | "rate_limited"
  | "verification_failed"
  | "server_error";

export class BookingApiError extends Error {
  readonly code: ApiErrorCode;
  readonly status: number;

  constructor(message: string, code: ApiErrorCode, status: number) {
    super(message);
    this.name = "BookingApiError";
    this.code = code;
    this.status = status;
  }
}
